import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ListBrowseToolbar,
  PageContainer,
  PageHeader,
  PageHeaderActionSecondary,
  PageHeaderActions,
  PageHeaderLeading,
  PageHeaderStack,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useArtifactCanvas,
  useDebouncedValue,
} from '@yeshuman/sdk'
import { BriefcaseBusiness, History, MoreHorizontal } from 'lucide-react'
import { toast } from 'sonner'

import {
  PlatformAreaError,
  usePlatformAction,
  usePlatformJobs,
  type PlatformJob,
} from '../../hooks/use-platform-area'
import { ImproveReviewCanvas } from '../improve/improve-review-canvas'
import { BackfillButton, BackfillProgress } from './backfill'
import {
  IncludeSimulatedSwitch,
  LastActionCell,
  ListPager,
  PLATFORM_PAGE_SIZE,
  PlatformOnlyNotice,
  SimulatedBadge,
  actionLogPath,
  formatScore,
} from './platform-area-parts'

const STATUS_LABEL: Record<PlatformJob['publication_status'], string> = {
  draft: 'Draft',
  published: 'Published',
  closed: 'Closed',
  archived: 'Archived',
}

function CompletenessCell({ job }: { job: PlatformJob }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-sm tabular-nums">{formatScore(job.completeness_score)}</span>
        {job.low_confidence ? <Badge variant="warningSoft" className="whitespace-nowrap">Low confidence</Badge> : null}
      </div>
      {job.completeness_missing.length > 0 ? (
        <p className="max-w-[14rem] text-xs text-muted-foreground">
          Missing {job.completeness_missing.join(', ').toLowerCase()}
        </p>
      ) : null}
    </div>
  )
}

function JobActions({
  job,
  onImprove,
  onRescore,
  onReparse,
  onRerun,
}: {
  job: PlatformJob
  onImprove: () => void
  onRescore: () => void
  onReparse: () => void
  onRerun: () => void
}) {
  const navigate = useNavigate()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghostMuted" size="iconSm" aria-label={`Actions for ${job.title}`}>
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => navigate(`/platform/jobs/${job.id}`)}>Open job</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onImprove}>Improve description</DropdownMenuItem>
        <DropdownMenuItem onSelect={onReparse}>Re-parse skills</DropdownMenuItem>
        <DropdownMenuItem onSelect={onRescore}>Rescore completeness</DropdownMenuItem>
        <DropdownMenuItem onSelect={onRerun}>Re-run match analysis</DropdownMenuItem>
        <DropdownMenuSeparator />
        {job.public_path ? (
          <DropdownMenuItem onSelect={() => navigate(job.public_path)}>View posting</DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={() => navigate(actionLogPath('job', job.id))}>
          Action history
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function PlatformJobsPage() {
  const [searchDraft, setSearchDraft] = useState('')
  const [status, setStatus] = useState('')
  const [includeSimulated, setIncludeSimulated] = useState(false)
  const [offset, setOffset] = useState(0)
  const [reparseJob, setReparseJob] = useState<PlatformJob | null>(null)
  const q = useDebouncedValue(searchDraft.trim(), 300)
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const action = usePlatformAction()

  useEffect(() => setOffset(0), [q, status, includeSimulated])

  const { data, isLoading, error } = usePlatformJobs({
    q,
    status,
    includeSimulated,
    offset,
    limit: PLATFORM_PAGE_SIZE,
  })

  if (error instanceof PlatformAreaError && (error.status === 401 || error.status === 403)) {
    return <PlatformOnlyNotice title="Jobs" />
  }

  const openImprove = (job: PlatformJob) =>
    openCanvas({
      title: `Improve job description: ${job.title}`,
      preventAutoFocus: true,
      content: (
        <ImproveReviewCanvas
          target={{ kind: 'opportunity', opportunityId: job.id, platform: true }}
          onClose={closeCanvas}
        />
      ),
    })

  const rescore = (job: PlatformJob) =>
    action.mutate(
      { kind: 'rescore', jobId: job.id },
      {
        onSuccess: (run) => toast(run.summary || 'Completeness rescored.'),
        onError: (err) => toast.error(err.message),
      },
    )

  const rerun = (job: PlatformJob) =>
    action.mutate(
      { kind: 'rerun-analysis', jobId: job.id },
      {
        onSuccess: () => toast('Re-running match analysis. The row updates when it finishes.'),
        onError: (err) => toast.error(err.message),
      },
    )

  const confirmReparse = () => {
    if (!reparseJob) return
    action.mutate(
      { kind: 'reparse-job', jobId: reparseJob.id },
      {
        onSuccess: (run) =>
          toast(run.status === 'running' ? 'Re-parsing skills. The row updates when it finishes.' : run.summary),
        onError: (err) => toast.error(err.message),
      },
    )
    setReparseJob(null)
  }

  const total = data?.total ?? 0

  return (
    <PageContainer className="space-y-6">
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          title="Jobs"
          description="Every job across all organisations. Improve, re-parse, rescore or re-run matches on any job without joining its organisation."
          icon={<BriefcaseBusiness className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              primary={<BackfillButton kind="completeness" primary />}
              secondary={
                <>
                  <BackfillButton kind="marketplace-skills" />
                  <PageHeaderActionSecondary asChild>
                    <Link to="/platform/actions?target_type=job">
                      <History className="h-4 w-4" aria-hidden />
                      Action log
                    </Link>
                  </PageHeaderActionSecondary>
                </>
              }
            />
          }
        />
        <BackfillProgress kind="completeness" />
        <BackfillProgress kind="marketplace-skills" />
        <div className="flex flex-col gap-3">
          <ListBrowseToolbar
            searchId="platform-jobs-search"
            searchPlaceholder="Search jobs or organisations…"
            searchValue={searchDraft}
            onSearchChange={setSearchDraft}
          />
          <div className="flex flex-wrap items-center gap-4">
            <Select value={status || 'all'} onValueChange={(value) => setStatus(value === 'all' ? '' : value)}>
              <SelectTrigger className="w-[160px]" aria-label="Status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {Object.entries(STATUS_LABEL).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <IncludeSimulatedSwitch
              id="platform-jobs-simulated"
              checked={includeSimulated}
              onCheckedChange={setIncludeSimulated}
            />
            <p className="text-sm text-muted-foreground" role="status">
              {isLoading ? 'Loading…' : `${total} job${total === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>
      </PageHeaderStack>

      {error && !(error instanceof PlatformAreaError && error.status < 500) ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : null}

      {isLoading ? <Skeleton className="h-64 w-full" /> : null}

      {!isLoading && !error && total === 0 ? (
        <p className="text-sm text-muted-foreground">No jobs match these filters.</p>
      ) : null}

      {data && data.results.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Completeness</TableHead>
                <TableHead>Skills</TableHead>
                <TableHead>Last Platform action</TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.results.map((job) => (
                <TableRow key={job.id}>
                  <TableCell className="align-top">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/platform/jobs/${job.id}`}
                        className="font-medium text-primary underline-offset-4 hover:underline"
                      >
                        {job.title}
                      </Link>
                      {job.is_simulation ? <SimulatedBadge /> : null}
                    </div>
                    <div className="text-xs text-muted-foreground">{job.organisation_name}</div>
                  </TableCell>
                  <TableCell className="align-top">
                    <Badge variant={job.publication_status === 'published' ? 'successSoft' : 'secondarySoft'}>
                      {STATUS_LABEL[job.publication_status] ?? job.publication_status}
                    </Badge>
                  </TableCell>
                  <TableCell className="align-top">
                    <CompletenessCell job={job} />
                  </TableCell>
                  <TableCell className="align-top whitespace-nowrap text-sm tabular-nums">
                    {job.required_skills} required
                    <div className="text-xs text-muted-foreground">{job.preferred_skills} preferred</div>
                  </TableCell>
                  <TableCell className="align-top">
                    <LastActionCell run={job.last_action} targetType="job" targetId={job.id} />
                  </TableCell>
                  <TableCell className="align-top text-right">
                    <JobActions
                      job={job}
                      onImprove={() => openImprove(job)}
                      onRescore={() => rescore(job)}
                      onReparse={() => setReparseJob(job)}
                      onRerun={() => rerun(job)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}

      <ListPager total={total} offset={offset} disabled={isLoading} onOffsetChange={setOffset} />

      <AlertDialog open={reparseJob !== null} onOpenChange={(open) => !open && setReparseJob(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-parse skills?</AlertDialogTitle>
            <AlertDialogDescription>
              The required and preferred skills on “{reparseJob?.title}” are read again from the job text and
              replace the current list. The title and description stay as they are. Matches for this job will
              refresh.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReparse}>Re-parse skills</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
